import React, { useMemo } from "react";
import { CheckCircle2 } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useDeviceAllocationHistory } from "@features/mx/resources/hook";

interface AllocationTabProps {
  resource: any;
  isAssigned: boolean;
}

export default function AllocationTab({ resource, isAssigned }: AllocationTabProps) {
  if (!resource) return null;

  const allocationColumns: TableColumn<any>[] = [
    { key: "index", header: "#", type: "text" },
    { key: "consumer", header: "Consumer", type: "text" },
    { key: "accountNo", header: "Account no", type: "text" },
    { key: "premise", header: "Premise", type: "text" },
    { key: "utilityService", header: "Utility service", type: "text" },
    { key: "resourceTypeDisplay", header: "Resource type", type: "text" },
    { key: "resourceSubTypeDisplay", header: "Resource sub type", type: "text" },
    { key: "allocatedOn", header: "Allocated on", type: "text" },
    { key: "deallocatedOn", header: "Deallocated on", type: "text" },
    {
      key: "status",
      header: "Status",
      type: "text",
      render: (val) => (
        <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${val === "Active"
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
          }`}>
          {val}
        </span>
      )
    }
  ];

  const { data: allocationData } = useDeviceAllocationHistory({
    meterNumber: resource?.meterNumber,
    deviceNo: resource?.deviceNo,
  });


  const currentAllocation = useMemo(() => {
    if (!allocationData || !Array.isArray(allocationData)) return null;
    return allocationData.find((item: any) => item.status === "Current") || allocationData[0];
  }, [allocationData]);

  const serviceAddress = useMemo(() => {
    if (!isAssigned || !currentAllocation?.consumerInfo?.territoryData?.service) return "—";
    const service = currentAllocation.consumerInfo.territoryData.service;
    return [
      service.address,
      service.premise,
      service.area,
      service.subArea,
      service.division,
      service.zone,
      service.county,
      service.state,
      service.country,
      service.zipcode
    ].filter(Boolean).join(", ");
  }, [isAssigned, currentAllocation]);

  const mappedHistoryData = React.useMemo(() => {
    if (!allocationData || !Array.isArray(allocationData)) return [];
    return allocationData.map((item: any, index: number) => ({
      index: index + 1,
      consumer: item.consumerInfo?.consumerName || "—",
      accountNo: item.consumerInfo?.consumerNo || "—",
      premise: item.consumerInfo?.territoryData?.service?.premise || "—",
      utilityService: resource?.utilityService || "—",
      meterType: resource?.type || "—",
      allocatedOn: item.assignDate || "—",
      deallocatedOn: item.deassignDate || "—",
      status: item.status || "—",
      resourceTypeDisplay: resource?.resourceTypeDisplay || "—",
      resourceSubTypeDisplay: resource?.resourceSubTypeDisplay || "—",
    }));
  }, [allocationData, resource]);

  return (
    <div className="space-y-6">
      {/* Allocation Status Banner */}
      <div className={`p-4 rounded-xl border flex items-start gap-3 ${isAssigned
        ? "bg-green-50 border-green-200 text-green-800"
        : "bg-gray-50 border-gray-200 text-gray-800"
        }`}>
        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-bold">{isAssigned ? "Resource is currently allocated" : "Resource is not allocated"}</h4>
          <p className="text-xs mt-0.5">
            {isAssigned
              ? "This resource is assigned to a consumer and active."
              : "No active allocation. This resource is available for assignment."
            }
          </p>
        </div>
      </div>

      {/* Current Allocation Section */}
      <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-5">
        <h4 className="text-xs font-bold text-blue-800 mb-4 tracking-wider">Current allocation</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ValueLabel label="Account no" value={isAssigned && currentAllocation?.consumerInfo?.consumerNo ? currentAllocation.consumerInfo.consumerNo : "—"} />
          <ValueLabel label="Name" value={isAssigned && currentAllocation?.consumerInfo?.consumerName ? currentAllocation.consumerInfo.consumerName : "—"} />
          <ValueLabel label="Phone" value={isAssigned && currentAllocation?.consumerInfo?.contactNumber ? currentAllocation.consumerInfo.contactNumber : "—"} />
          <ValueLabel label="Email" value={isAssigned && currentAllocation?.consumerInfo?.email ? currentAllocation.consumerInfo.email : "—"} />

          <ValueLabel
            label="Status"
            value={
              <Badge className={`mt-0.5 border ${isAssigned ? "bg-green-50 text-green-700 border-green-150" : "bg-gray-50 text-gray-700 border-gray-250"
                }`}>
                {isAssigned ? (currentAllocation?.status || "Assigned") : "Unassigned"}
              </Badge>
            }
          />

          <ValueLabel label="Unit" value={currentAllocation?.unit || resource?.unit || "—"} />
          <ValueLabel label="Assigned on" value={isAssigned && currentAllocation?.assignDate ? currentAllocation.assignDate : "—"} />
          <ValueLabel label="Assigned by" value={isAssigned ? (resource.createdBy || "NA") : "—"} />

          <div className="col-span-2 md:col-span-4">
            <ValueLabel
              label="Service address"
              value={serviceAddress}
            />
          </div>
        </div>
      </div>

      {/* Allocation History */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-3 tracking-wider">Allocation history</h4>
        <div className="border border-gray-150 rounded-xl overflow-hidden">
          <GenericTable
            columns={allocationColumns}
            data={mappedHistoryData}
            rowKey="index"
          />
        </div>
      </div>
    </div>
  );
}
